import React, { useContext, useEffect, useState } from 'react';
import { useTheme } from '../Contexts/ThemeContext';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
  Button,
  CompactSidebarNavTrigger,
  DropdownMenu,
  Icon,
  Page,
  Select,
  SidebarNav,
  SidebarNavContent,
  SidebarNavFooter,
  SidebarNavHeader,
  SidebarNavMenu,
  SidebarNavMenuButton,
  SidebarNavMenuItem,
} from '@aidkitorg/component-library';
import { NavigationNode } from '@aidkitorg/types/lib/translation/permissions';
import { langToWord, useLocalizedStrings } from '../Localization';
import InterfaceContext, { PublicConfigurationContext, SupportedLanguage, UserInfoContext } from '../Context';
import { SelectOptionType } from '@aidkitorg/component-library/dist/types/src/Select';
import { Text } from '@aidkitorg/types/lib/survey';
import { IconName } from '@aidkitorg/component-library/dist/types/src/Icon';

export type AdminPanelPageProps = React.PropsWithChildren<{
  headerContent?: React.ReactNode,
  breadcrumbs?: React.ReactNode,
}>

type LanguageDropdownProps = {
  languages: Array<SupportedLanguage>
}

const LanguageDropdown: React.FC<LanguageDropdownProps> = ({ languages }) => {
  const interfaceContext = useContext(InterfaceContext)

  const [selected, setSelected] = useState<SelectOptionType>({
    value: interfaceContext.lang,
    label: langToWord(interfaceContext.lang, 'Name')
  })
  const languageOptions: Array<SelectOptionType> = []
  languages.forEach((l) => {
    languageOptions.push({
      value: l as string,
      label: langToWord(l, 'Name')
    })
  })

  useEffect(() => {
    interfaceContext.setLanguage(selected.value as SupportedLanguage)
  }, [selected, interfaceContext])

  return (
    <Select
      className="border border-gray-300"
      label={<Icon role="decorative" name="LanguageIcon" />}
      labelLocation='inline'
      description="Select a language"
      options={languageOptions}
      value={selected}
      onChange={(value) => { setSelected(value) }}
    />
  )
}

export const AdminPanelPage: React.FC<AdminPanelPageProps> = ({
  headerContent,
  breadcrumbs,
  children
}) => {
  const history = useHistory()

  // TODO: uncomment this before releasing
  // if (publicConfig && publicConfig.adminPanel === false){
  //   history.push("/");
  // }

  const L = useLocalizedStrings()

  const AdminPanelNav = () => {
    // TODO: update Theme provider to take in a size for the logo
    const { logo, languages } = useTheme();
    const { path } = useRouteMatch();
    const publicConfig = useContext(PublicConfigurationContext);
    const L = useLocalizedStrings();

    const NAV_ITEMS: Array<NavigationNode & { iconName: IconName }> = [
      {
        name: L.admin_panel.nav.dashboard_home,
        href: '/admin-panel/overview',
        iconName: 'HomeIcon'
      },
      {
        name: L.admin_panel.nav.payments,
        href: '/admin-panel/payments',
        iconName: 'CurrencyDollarIcon'
      },
      // TODO: uncomment this out once Accounts is released to design partners
      // {
      //   name: "Accounts",
      //   href: '/admin-panel/accounts',
      //   iconName: 'WalletIcon'
      // }
    ]

    const getTranslatedText = (lang?: keyof Text, value?: string | Text) => {
      if (typeof value === 'string' || !value) {
        return value;
      }
      return value[lang ?? 'en'] ?? value['en'];
    }

    const interfaceContext = useContext(InterfaceContext)

    const dropdownOptions = [
      { label: L.menu.logout, callback: () => { window.location.assign('/logout') } }
    ]

    if (publicConfig.adminPasswords) {
      dropdownOptions.push({ label: L.admin.account_settings.title, callback: () => { window.location.assign('/admin/account_settings') } })
    }

    const history = useHistory()

    return (
      <SidebarNav
        collapsible='icon'
        className="bg-white"
      >
        <SidebarNavHeader className='py-4'>
          <div>
            <LanguageDropdown languages={languages} />
          </div>
          <div className="pt-2">
            {logo}
          </div>
        </SidebarNavHeader>

        <SidebarNavContent>
          <SidebarNavMenu>
            {NAV_ITEMS.map((item, idx) => {
              return (
                <SidebarNavMenuItem key={idx}>
                  <SidebarNavMenuButton href={item.href} onClick={(e) => {
                    history.push(item.href)
                    e.preventDefault()
                  }}
                  isActive={path.includes(item.href)}>
                    <Icon name={item.iconName} type="solid" role="decorative" />
                    <span key={idx}>{getTranslatedText(interfaceContext.lang, item.name)}</span>
                  </SidebarNavMenuButton>
                </SidebarNavMenuItem>
              )
            })}
          </SidebarNavMenu>
        </SidebarNavContent>

        <SidebarNavFooter>
          {/* TODO: update DropdownMenu here to include avatar and user's name */}
          <DropdownMenu anchor="top" label="Menu" options={dropdownOptions} />
        </SidebarNavFooter>
      </SidebarNav>
    )
  }

  const header = (
    <div className="w-full">
      <div className="flex gap-4 items-center">
        <CompactSidebarNavTrigger />
        {breadcrumbs && (
          <div className="flex gap-2 items-center">
            {breadcrumbs}
          </div>
        )}
      </div>
      {headerContent && (
        <div className="flex justify-between items-center">
          {headerContent}
        </div>
      )}
    </div>
  )

  const footer = (
    <div className="w-full flex justify-between">
      <div>
        <Button variant="link" href="/privacy.html" openInNewWindow>
          {L.privacy_policy}
        </Button>
      </div>
      <div>
        <Button variant="link" href="/tos.html" openInNewWindow>
          {L.terms_and_conditions}
        </Button>
      </div>
    </div>
  )

  return (
    <Page
      className="bg-gray-50"
      header={header}
      sidebar={
        <AdminPanelNav />
      }
      footer={footer}
    >
      {children}
    </Page>
  )
}